import React, { useState } from 'react';
import FormField from '../../components/common/FormField';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import { FirearmOwnerDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import { ValidationRules, useFormValidation } from '../../hooks/useFormValidation';

interface FirearmOwnerFormProps {
  initialValues: FirearmOwnerDTO;
  onSubmit: (data: FirearmOwnerDTO) => void;
  onCancel: () => void;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information',
  },
  settings: {
    id: 'settings',
    title: 'Settings',
  },
} as const;

const FirearmOwnerForm: React.FC<FirearmOwnerFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState<FirearmOwnerDTO>(initialValues);

  const validationRules: ValidationRules = {
    title: [
      { required: true, message: 'Title is required' },
    ],
    displayPosition: [
      { required: true, message: 'Position is required' },
      { 
        custom: (value) => Number(value) >= 0, 
        message: 'Position must be a positive number' 
      }
    ]
  };

  const { errors, validate } = useFormValidation<FirearmOwnerDTO>(validationRules);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form sections={sections} onSave={handleSave} onCancel={onCancel}>
      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="title"
          label="Title"
          type="text"
          value={formData.title || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Title"
          required
          error={errors.title}
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.settings.id}
        title={FORM_SECTIONS.settings.title}
      >
        <FormField
          id="displayPosition"
          label="Position"
          type="number"
          value={formData.displayPosition ?? ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Display Position"
          helper="Lower numbers will be displayed first."
          required
          error={errors.displayPosition}
        />
        <FormField
          id="isActive"
          label="Active?"
          type="checkbox"
          value={formData.isActive || ''}
          onChange={(e) => handleChange(e, setFormData)}
          helper="Check to make record active."
        />
      </FormSection>
    </Form>
  );
};

export default FirearmOwnerForm;
